import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';

import LoadablePanel from 'components/hoc/loadable/Panel';
import ClickToDial from 'components/ClickToDial';
import { getPopupClass } from 'app/PopupHolder/popupRepository';
import selectPopups from 'app/PopupHolder/selectors';
import PropertyDetailPopup from 'app/Property/Detail/Detail';
import { selectProfile, selectInvestorOutreachNavVisible, selectUserId } from 'data/user';
import { isLegacyAccountPlan } from 'data/user/selectors';
import {
  selectInvestorSearched,
  selectLoading,
  fetchInvestorOutreachEntitlement,
  selectInvestorOutreachEntitled,
  selectInvestorOutreachEntitlementLoaded,
  selectInvestorOutreachEntitlementLoading,
  selectInvestorOutreachEntitlementError,
} from 'data/investors';
import ActivateSubscriptionPopup from 'app/AccountNew/modals/ActivateSubscription';
import { openLegacyUpgradePopup } from 'app/AccountNew/InvestorOutreach/legacyUpgradePopup';
import { isPropStream } from 'utils/brand';

import Map from './Map';
import SearchFilter from './SearchFilter';
import EmptyState from './EmptyState';
import Results from './Results';
import InvestorOutreachUpsell from './UpsellOverlay';
import UnlockConfirmModal from './UnlockConfirmModal';
import css from './style.scss';

// One route for subscribe/unsubscribe, matching the S6 Account card (ATAI-271).
const INVESTOR_OUTREACH_PURCHASE_ROUTE = '/accountnew/manageinvestoroutreach/subscribe';

// The steps of a trial holder's Unlock Now flow (Figma 1376:152690). They run one at a time so
// that a cancel at any point lands the customer back on the upsell rather than on the bare
// locked page:
//
//   UPSELL --Unlock Now--> CONFIRM --Unlock--> ACTIVATE --payment taken--> DONE (subscribe page)
//      ^                      |                   |
//      +-------Cancel---------+-------Cancel------+
//
// CONFIRM stacks on top of the still-open upsell (HIGHEST vs MEDIUM), so cancelling it needs no
// re-open. ACTIVATE is the shared Activate Subscription modal, hard-coded to MEDIUM -- the same
// queue the upsell sits in, and this app renders only the head of each queue -- so the upsell
// has to come down for it, and go back up if it is cancelled.
const UNLOCK_STEP = {
  UPSELL: 'upsell',
  CONFIRM: 'confirm',
  ACTIVATE: 'activate',
  DONE: 'done',
};

// Property Details (opened from a card, the Profile's property table or a map
// card) mounts its own <ClickToDial>, which layers inside its modal. While it is
// showing, the page's host below must not also be mounted -- both would render
// Ready to Call and fetch click-to-dial-data. A popup is identified by its
// registered component's `open` static, which withRouter hoists unchanged onto
// the default export (its WrappedComponent is NOT the registered component).
// Read at call time: Detail.js imports back into this page via routes.
const selectPropertyDetailShowing = (state) => {
  const queues = selectPopups(state)?.get('queues');
  return Boolean(
    queues &&
    queues.some((queue) => {
      const popupId = queue.getIn([0, 'id']);
      return Boolean(popupId) && getPopupClass(popupId)?.open === PropertyDetailPopup.open;
    })
  );
};

// The Investor Search surface (ATAI-110 / S4) with the S7 entitlement gating layered
// on (ATAI-260): who may enter, and the navigate-then-overlay upsell for a primary
// account holder who has not subscribed yet.
//
// withRouter for `history`, not useHistory: this app pairs react-router-dom 5.3.4 with
// React 18, where v5's context hooks read back null (see Results/index.js).
const InvestorOutreach = ({ history }) => {
  const dispatch = useDispatch();

  const profile = useSelector(selectProfile);
  const navVisible = useSelector(selectInvestorOutreachNavVisible);
  const searched = useSelector(selectInvestorSearched);
  const loading = useSelector(selectLoading);
  const userId = useSelector(selectUserId);
  const propertyDetailShowing = useSelector(selectPropertyDetailShowing);

  // Hosts the shared "Ready to Call" flow the cards' and Investor
  // Profile's phone numbers open (./useDialInvestorPhone). Same gate as
  // Contacts/ContactProfile -- dispatching openClickToDial does nothing on a
  // screen that doesn't render this. Steps aside while Property Details (and
  // so its own instance) is showing; see selectPropertyDetailShowing.
  const shouldRenderClickToDial =
    isPropStream() &&
    ['FULL', 'TEAM_FULL', 'TEAM'].includes(profile?.get?.('type')) &&
    !propertyDetailShowing;

  // Roles follow the S6 card (AccountNew/Sections/InvestorOutreach.js) so the §2 matrix
  // stays consistent across surfaces.
  const isScout = Boolean(profile?.get('subUser') && profile?.get('scoutUser'));
  const isTeamMember = ['TEAM', 'TEAM_FULL'].includes(profile?.get('type'));
  const isPrimaryHolder = !isTeamMember && !isScout;

  // Entitlement is read from the `investorOutreach` store slice, which fetches the S2 cancel-preview
  // once and caches it — so revisiting this page no longer re-calls the API or re-shows the loader.
  // (The profile carries no reliable subscription flag; the real fix is the BE surfacing
  // `investorOutreachSubscribed` on it, which would make this synchronous.) A team member who reaches
  // this route is already entitled — navVisible encodes the permission on a subscribed account.
  const ioEntitled = useSelector(selectInvestorOutreachEntitled);
  const ioLoaded = useSelector(selectInvestorOutreachEntitlementLoaded);
  const ioLoading = useSelector(selectInvestorOutreachEntitlementLoading);
  const ioError = useSelector(selectInvestorOutreachEntitlementError);

  useEffect(() => {
    if (navVisible && isPrimaryHolder && userId) {
      dispatch(fetchInvestorOutreachEntitlement(userId));
    }
  }, [navVisible, isPrimaryHolder, userId, dispatch]);

  // ATAI-448: read at click time through a ref, not an effect dependency -- the plan details can
  // finish loading after the overlay is already up, and re-running the effect below would close
  // and reopen it.
  const isLegacyAccount = useSelector(isLegacyAccountPlan);
  const isLegacyAccountRef = useRef(isLegacyAccount);
  isLegacyAccountRef.current = isLegacyAccount;

  const isEntitled = isPrimaryHolder ? Boolean(ioEntitled) : true;
  const showUpsell = navVisible && isPrimaryHolder && ioLoaded && !ioEntitled && !ioError;

  // Mask the feature with a loader whenever the primary holder's entitlement is being fetched —
  // whether it has not loaded yet or a forced refresh (after subscribe/unsubscribe) is in flight —
  // so the screen never flashes usable before the overlay resolves. A cached revisit makes no call
  // (loading stays false), so no loader. Team members never wait; their access is known from
  // navVisible.
  const entitlementPending = isPrimaryHolder && (ioLoading || !ioLoaded);

  // Where the Unlock Now flow has got to; see UNLOCK_STEP. Only a trial holder walks past the
  // first step -- a paying holder's Unlock Now still goes straight to the S8 purchase page.
  const [unlockStep, setUnlockStep] = useState(UNLOCK_STEP.UPSELL);

  // The overlay stays up underneath the confirmation dialog and only comes down for the
  // Activate Subscription modal. This is derived into a boolean rather than read off
  // `unlockStep` inside the effect so that UPSELL -> CONFIRM does not re-run it: closing and
  // re-opening the overlay would reload the product tour embedded in it.
  const upsellVisible =
    showUpsell && (unlockStep === UNLOCK_STEP.UPSELL || unlockStep === UNLOCK_STEP.CONFIRM);

  // Navigate-then-overlay (ATAI-260 / S7): the unsubscribed primary holder lands on the
  // locked feature route with the upsell drawn over it. Unlock Now's trial-vs-purchase
  // branch is chosen inside the overlay from planDetails.trialAvailable; here we just hand
  // off the two destinations, mirroring the S6 card. A successful unlock clears the overlay
  // in place (entitlement flips showUpsell false).
  useEffect(() => {
    if (!upsellVisible) return undefined;

    // A legacy plan can't buy Investor Outreach: Unlock Now shows the upgrade prompt instead
    // (ATAI-448), whichever branch (purchase / trial conversion) it would otherwise take.
    const promptLegacyUpgrade = () =>
      dispatch(openLegacyUpgradePopup({ onNavigate: (path) => history.push(path) }));

    dispatch(
      InvestorOutreachUpsell.open({
        onPurchase: () =>
          isLegacyAccountRef.current
            ? promptLegacyUpgrade()
            : history.push(INVESTOR_OUTREACH_PURCHASE_ROUTE), // S8 purchase page (ATAI-261)
        onStartTrialConversion: () =>
          isLegacyAccountRef.current ? promptLegacyUpgrade() : setUnlockStep(UNLOCK_STEP.CONFIRM),
      })
    );

    return () => dispatch(InvestorOutreachUpsell.close());
  }, [upsellVisible, dispatch, history]);

  // Step 2 -- "Unlock Investor Outreach" confirmation, drawn over the overlay.
  useEffect(() => {
    if (unlockStep !== UNLOCK_STEP.CONFIRM) return undefined;

    dispatch(
      UnlockConfirmModal.open({
        onCancel: () => setUnlockStep(UNLOCK_STEP.UPSELL),
        onConfirm: () => setUnlockStep(UNLOCK_STEP.ACTIVATE),
      })
    );

    return () => dispatch(UnlockConfirmModal.close());
  }, [unlockStep, dispatch]);

  // Step 3 -- the shared Activate Subscription modal converts the trial. Completing it does not
  // buy Investor Outreach, it only ends the trial, so success hands the customer on to the
  // Investor Outreach subscription page to finish the purchase.
  useEffect(() => {
    if (unlockStep !== UNLOCK_STEP.ACTIVATE) return undefined;

    dispatch(
      ActivateSubscriptionPopup.open({
        upgradedFromCallSettings: false,
        // S4 · ATAI-490: success moves straight on to the purchase page, so no "Success" message first.
        skipSuccessMessage: true,
        onNavigate: (path) => history.push(path),
        // PopupHolder invokes a popup's `onClose` on EVERY close, success included, and the
        // modal then calls it a second time itself -- so "cancelled" is really `onClose` with
        // no `onSuccess` behind it. Both setters are idempotent and React 18 batches the pair,
        // so the success path settles on DONE and never flashes the overlay back up.
        onClose: () => setUnlockStep(UNLOCK_STEP.UPSELL),
        onSuccess: () => {
          setUnlockStep(UNLOCK_STEP.DONE);
          history.push(INVESTOR_OUTREACH_PURCHASE_ROUTE);
        },
      })
    );

    return () => dispatch(ActivateSubscriptionPopup.close());
  }, [unlockStep, dispatch, history]);

  // Team member without the permission, Scout, white-label, or anyone deep-linking where the
  // nav item is hidden: nothing to show (they can't buy) — send them back to search.
  if (!navVisible) return <Redirect to="/search" />;

  return (
    // isHover keeps the search page visible in the background with a dim overlay + spinner on top,
    // rather than blanking it while the primary holder's entitlement loads.
    <LoadablePanel loading={entitlementPending} isHover>
      <section className={css.container}>
        <div className={css.leftPane}>
          <div className={css.searchFilterArea}>
            <SearchFilter canSearch={isEntitled} />
          </div>
          <div className={css.mapArea}>
            <Map />
          </div>
        </div>
        <LoadablePanel loading={loading}>
          <div className={css.rightPane}>
            {/* Overlays the shared Loader and hides the pane's content while a
                search is in flight, so neither the empty state nor a previous
                result set shows through. */}
            {searched ? <Results /> : <EmptyState />}
          </div>
        </LoadablePanel>
        {shouldRenderClickToDial && <ClickToDial profile={profile} />}
      </section>
    </LoadablePanel>
  );
};

export default withRouter(InvestorOutreach);
